import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { Typography } from '@mui/material';
import { GreenerEyeForm } from 'components/forms/GreenerEyeForm';
import Panel from 'components/panels/Panel';
import useAuth from 'hooks/useAuth';
import { formatQueryKey, useGQLRequest } from 'hooks/useGQLQuery';
import { useSnackbar } from 'notistack';
import { CREATE_GREENER_EYE, GREENER_EYES_KEY } from 'services/gql/greenerEyes';
import { createErrorFieldObject, createGenericErrorMessage } from 'services/gql/parseGqlErrors';
import { canCreateEye } from 'utils/authorization';

export const Component = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const performGQLrequest = useGQLRequest();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { error, mutateAsync, isLoading } = useMutation(
    async (data) => {
      return await performGQLrequest(CREATE_GREENER_EYE, {
        greenerEye: {
          name: data.name,
          externalIdentifier: data.externalIdentifier,
          description: data.description,
          owningOrganisationId: data.owningOrganisation.organisationId,
        },
      });
    },
    {
      onSuccess: (data) => {
        const externalIdentifier = data?.createGreenerEye?.greenerEye?.externalIdentifier;
        enqueueSnackbar(
          t('genericSuccesses.create', {
            entity: t('entities:greenerEye.nameSingular').toLowerCase(),
            identifier: externalIdentifier,
          }),
          { variant: 'success' }
        );
        queryClient.invalidateQueries(formatQueryKey(GREENER_EYES_KEY));
        navigate('/greener-eyes');
      },
      onError: (error, { externalIdentifier } = {}) => {
        const parsedMessage = createGenericErrorMessage(error) || 'create';
        enqueueSnackbar(
          t(`main:genericErrors.${parsedMessage}`, {
            entity: t('entities:greenerEye.nameSingular').toLowerCase(),
            identifier: externalIdentifier,
          }),
          { variant: 'error' }
        );
      },
    }
  );

  const errorFieldObject = useMemo(() => createErrorFieldObject(error), [error]);

  return (
    <>
      <Typography variant="h3" mb={2}>
        {t('entities:greenerEye.new')}
      </Typography>
      <Panel title={t('entities:greenerEye.details')} titleVariant="h3">
        <GreenerEyeForm
          onSubmit={mutateAsync}
          isLoading={isLoading}
          fieldErrors={errorFieldObject}
          isEditable={canCreateEye(user?.role)}
        />
      </Panel>
    </>
  );
};

Component.displayname = 'AddGreenerEye';
